import * as RadixSlider from '@radix-ui/react-slider';
import { Tooltip, InfoIcon } from './Tooltip';

interface SliderProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  label: string;
  tooltip?: string;
  formatValue?: (n: number) => string;  // e.g. (n) => `${n} TB`
  logarithmic?: boolean;  // for execution counts spanning many orders
  markers?: string[];     // e.g. ['1GB', '100GB', '1TB', '100TB']
}

export function Slider({
  value,
  onChange,
  min,
  max,
  step = 1,
  label,
  tooltip,
  formatValue = (n) => n.toString(),
  logarithmic = false,
  markers
}: SliderProps) {
  
  // Mapping for logarithmic scale


  const fromLog = (logV: number) => {
    if (!logarithmic) return logV;
    const pos = ((Math.log(logV) - Math.log(min)) / (Math.log(max) - Math.log(min))) * 100;
    return pos;
  };

  // If logarithmic, the component's internal value is 0-100
  const internalValue = logarithmic ? [fromLog(value)] : [value];
  const internalMin = logarithmic ? 0 : min;
  const internalMax = logarithmic ? 100 : max;
  const internalStep = logarithmic ? 0.01 : step;

  const handleInternalChange = (vals: number[]) => {
    const val = vals[0];
    if (logarithmic) {
      const realVal = Math.round(
        Math.exp(Math.log(min) + (val / 100) * (Math.log(max) - Math.log(min)))
      );
      onChange(realVal);
    } else {
      onChange(val);
    }
  };

  return (
    <div className="flex flex-col w-full gap-2">
      {/* Label Row */}
      <div className="flex flex-row items-center justify-between mb-1">
        <div className="flex items-center gap-1">
          <label className="text-[12px] text-azure-muted uppercase tracking-wider font-medium">
            {label}
          </label>
          {tooltip && (
            <Tooltip content={tooltip}>
              <InfoIcon />
            </Tooltip>
          )}
        </div>
        <span className="text-[14px] font-mono text-azure-text">
          {formatValue(value)}
        </span>
      </div>

      <RadixSlider.Root
        className="relative flex items-center select-none touch-none w-full h-11"
        value={internalValue}
        onValueChange={handleInternalChange}
        min={internalMin}
        max={internalMax}
        step={internalStep}
      >
        <RadixSlider.Track className="relative grow rounded-full h-[6px]" style={{ backgroundColor: 'var(--slider-track)' }}>
          <RadixSlider.Range className="absolute bg-azure-blue rounded-full h-full" />
        </RadixSlider.Track>
        <RadixSlider.Thumb
          className="
            block w-[22px] h-[22px] bg-azure-blue border-[3px] border-[#c7e0f4]
            rounded-full focus:outline-none focus:ring-2 focus:ring-azure-blue
            hover:scale-110 transition-all cursor-pointer shadow-none
          "
          aria-label={label}
        />
      </RadixSlider.Root>

      {/* Markers Row — spread horizontally along the track */}
      {markers && (
        <div
          className="flex flex-row justify-between px-0 mt-1"
          style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
        >
          {markers.map((m, i) => (
            <span
              key={i}
              className="text-[11px] leading-none"
              style={{ color: 'var(--text-tertiary)' }}
            >
              {m}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
